import webRtcSupport from 'webrtcsupport';
import { Log } from './log';

export class VideoUtils {
    private static isIos(): boolean {
        return navigator.userAgent.match(/iPhone|iPad/i) !== null;
    }

    private static isAndroid(): boolean {
        return /android/i.test(navigator.userAgent);
    }

    private static isFirefox(): boolean {
        return /firefox/i.test(navigator.userAgent);
    }

    private static isChrome(): boolean {
        return (
            /google/i.test(navigator.vendor) &&
            !/edg/i.test(navigator.userAgent)
        );
    }

    private static isSafari(): boolean {
        return (
            /Apple/i.test(navigator.vendor) &&
            /safari/i.test(navigator.userAgent)
        );
    }

    private static isChromiumEdge(): boolean {
        return (
            /google/i.test(navigator.vendor) && /edg/i.test(navigator.userAgent)
        );
    }

    public static getBrowser(): Browser {
        if (this.isChrome()) {
            return Browser.Chrome;
        } else if (this.isFirefox()) {
            return Browser.Firefox;
        } else if (this.isChromiumEdge()) {
            return Browser.ChromiumEdge;
        }
        if (this.isSafari()) {
            return Browser.Safari;
        } else {
            return Browser.Unknown;
        }
    }

    public static isWebRtcSupported(): boolean {
        return webRtcSupport.support;
    }

    public static isVideoSupported(): boolean {
        if (this.isIos()) {
            return false;
        }

        if (
            this.isChrome() ||
            this.isChromiumEdge() ||
            this.isSafari() ||
            this.isFirefox()
        ) {
            return true;
        }

        return false;
    }

    private static isLongOffsetSupported() {
        try {
            new Date().toLocaleDateString(undefined, {
                timeZoneName: 'longOffset',
            });
            return true;
        } catch {
            return false;
        }
    }

    public static redirectIfUnsupported(): boolean {
        if (this.isVideoSupported() && this.isLongOffsetSupported()) {
            return;
        }

        const debug = {
            userAgent: navigator.userAgent,
            vendor: navigator.vendor,
            browserVersion: webRtcSupport.browserVersion,
            support: webRtcSupport.support,
            supportRTCPeerConnection: webRtcSupport.supportRTCPeerConnection,
            supportVp8: webRtcSupport.supportVp8,
            supportGetUserMedia: webRtcSupport.supportGetUserMedia,
            supportDataChannel: webRtcSupport.supportDataChannel,
            supportWebAudio: webRtcSupport.supportWebAudio,
            supportMediaStream: webRtcSupport.supportMediaStream,
            supportScreenSharing: webRtcSupport.supportScreenSharing,
            prefix: webRtcSupport.prefix,
        };

        Log.warn('Unsupported browser', debug);

        let errorPage = '/unsupported/desktop';

        if (this.isAndroid()) {
            errorPage = '/unsupported/android';
        }

        if (this.isIos()) {
            errorPage = '/unsupported/ios';
        }

        // Use XMLHttpRequest to maximize browser compatibility
        const request = new XMLHttpRequest();
        request.onreadystatechange = (): void => {
            if (request.readyState == 4) {
                document.getElementsByTagName('html')[0].innerHTML =
                    request.responseText;
            }
        };
        request.open('GET', errorPage, true);
        request.send();
    }

    public static isMediaElementSinkSupported(): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return typeof (new Audio() as any).sinkId !== 'undefined';
    }
}

export enum Browser {
    Chrome,
    ChromiumEdge,
    Firefox,
    Safari,
    Unknown,
}
